// File: src/components/artistDashboard/labelServices/CampaignBuilderModal.js

import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { httpsCallable } from 'firebase/functions'; 
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { functions } from '../../../services/firebase';
import PressPostPreview from '../../press/PressPostPreview';
import toast from 'react-hot-toast';
import { useProfile } from '../../../context/ProfileContext';

const MAX_FILE_SIZE_MB = 50;

// Ordered tier hierarchy for progressive inheritance (Lower -> Higher)
const TIER_HIERARCHY = ['subscribers', 'fans', 'supafans', 'ultrafans'];

const Spinner = () => (
    <div className="animate-spin rounded-full h-4 w-4 border-2 border-t-transparent border-white border-solid"></div>
);

const CampaignBuilderModal = ({ isOpen, onClose, artistId, artistProfile, postToEdit }) => {
    const { isDarkMode } = useProfile();

    const [postTitle, setPostTitle] = useState('');
    const [postBody, setPostBody] = useState('');
    const [externalUrl, setExternalUrl] = useState('');
    const [urlBoxTitle, setUrlBoxTitle] = useState(''); 
    const [mediaFile, setMediaFile] = useState(null);
    const [existingMediaUrl, setExistingMediaUrl] = useState(null);
    const [selectedSongId, setSelectedSongId] = useState('');
    const [songBoxTitle, setSongBoxTitle] = useState(''); 

    // Progressive Audience Gating Matrix State
    const [targetAudience, setTargetAudience] = useState({
        everyone: true,
        subscribers: false,
        fans: false,
        supafans: false,
        ultrafans: false
    });

    const [allArtistSongs, setAllArtistSongs] = useState([]);
    const [isFetchingSongs, setIsFetchingSongs] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isEditMode = !!postToEdit;

    // Computed variable: find active selected song object for preview
    const selectedSong = useMemo(() => 
        allArtistSongs.find(song => song.id === selectedSongId),
        [allArtistSongs, selectedSongId]
    );

    const getArtistSongsDetail = useMemo(() => {
        return httpsCallable(functions, 'getArtistSongsDetail');
    }, []);

    useEffect(() => {
        if (isOpen && artistId) {
            setIsFetchingSongs(true);
            getArtistSongsDetail({ artistId })
                .then(response => {
                    const songs = response.data.songs || [];
                    setAllArtistSongs(songs);
                })
                .catch(err => console.error("Error fetching song details:", err))
                .finally(() => setIsFetchingSongs(false));
        }
    }, [isOpen, artistId, getArtistSongsDetail]);
    
    useEffect(() => {
        if (!searchTerm || allArtistSongs.length === 0) {
            setSearchResults([]);
            return;
        }
        const results = allArtistSongs.filter(song =>
            song.title?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setSearchResults(results);
    }, [searchTerm, allArtistSongs]);

    // Progressive Audience Toggle Handler
    const handleAudienceToggle = (clickedKey) => {
        setTargetAudience(prev => {
            if (clickedKey === 'everyone') {
                return {
                    everyone: true,
                    subscribers: false,
                    fans: false,
                    supafans: false,
                    ultrafans: false
                };
            }

            const clickedIndex = TIER_HIERARCHY.indexOf(clickedKey);
            const isLowestActiveTier = prev[clickedKey] && (clickedIndex === 0 || !prev[TIER_HIERARCHY[clickedIndex - 1]]);

            if (isLowestActiveTier) {
                return {
                    everyone: true,
                    subscribers: false,
                    fans: false,
                    supafans: false,
                    ultrafans: false
                };
            }

            const updated = {
                everyone: false,
                subscribers: false,
                fans: false,
                supafans: false,
                ultrafans: false
            };

            for (let i = clickedIndex; i < TIER_HIERARCHY.length; i++) {
                updated[TIER_HIERARCHY[i]] = true;
            }

            return updated;
        });
    };

    const handleSearchTermChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        setIsDropdownVisible(true);

        if (!val.trim()) {
            setSelectedSongId('');
            setSearchResults([]);
        }
    };

    // Reset state when closed
    useEffect(() => {
        if (!isOpen) {
            setPostTitle('');
            setPostBody('');
            setExternalUrl('');
            setUrlBoxTitle('');
            setSongBoxTitle('');
            setMediaFile(null);
            setExistingMediaUrl(null);
            setSelectedSongId('');
            setSearchTerm('');
            setTargetAudience({
                everyone: true,
                subscribers: false,
                fans: false,
                supafans: false,
                ultrafans: false
            });
        }
    }, [isOpen]);

    // Populate state when editing an existing post
    useEffect(() => {
        if (isOpen && isEditMode && postToEdit) {
            setPostTitle(postToEdit.title || '');
            setPostBody(postToEdit.text || '');
            setExternalUrl(postToEdit.externalUrl || '');
            setUrlBoxTitle(postToEdit.urlBoxTitle || '');
            setSelectedSongId(postToEdit.songId || '');
            setSongBoxTitle(postToEdit.songBoxTitle || '');
            setExistingMediaUrl(postToEdit.mediaUrl || null);
            
            if (postToEdit.targetAudience) {
                setTargetAudience(prev => ({ ...prev, ...postToEdit.targetAudience }));
            }
        }
    }, [isOpen, isEditMode, postToEdit]);

    const handleSelectSong = (song) => {
        setSelectedSongId(song.id);
        setSearchTerm(song.title);
        setIsDropdownVisible(false);
        setSearchResults([]);
    };

    // 🌟 UNIFIED PUBLISH / UPDATE HANDLER
    const handlePublishPost = async () => {
        if (!postTitle.trim() && !postBody.trim()) {
            toast.error("Please provide a title or body text for your post.");
            return;
        }

        setIsSubmitting(true);
        let mediaUrl = existingMediaUrl; // Retain existing media URL if no new file is uploaded
        const auth = getAuth();

        try {
            // 1. Handle file upload if a new media file was selected
            if (mediaFile) {
                if (!auth.currentUser) throw new Error("User not authenticated.");
                const token = await auth.currentUser.getIdToken();

                const generateUrlResponse = await fetch("https://us-central1-atomic-musicapp.cloudfunctions.net/generatePostMediaUploadUrl", {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        artistId: artistId,
                        fileName: mediaFile.name,
                        contentType: mediaFile.type
                    })
                });

                if (!generateUrlResponse.ok) {
                    throw new Error((await generateUrlResponse.json()).error || "Could not get upload permission.");
                }

                const { uploadUrl, finalPath } = await generateUrlResponse.json();

                const uploadResponse = await fetch(uploadUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': mediaFile.type },
                    body: mediaFile
                });

                if (!uploadResponse.ok) {
                    throw new Error("File upload failed.");
                }
                
                const storage = getStorage();
                mediaUrl = await getDownloadURL(ref(storage, finalPath));
            }

            // 2. Prepare payload
            const payload = {
                artistId,
                title: postTitle,
                text: postBody, 
                externalUrl: externalUrl.trim() || null,
                urlBoxTitle: urlBoxTitle.trim() || null,
                mediaUrl: mediaUrl,
                songId: selectedSongId || null,
                songBoxTitle: songBoxTitle.trim() || null,
                songData: selectedSong ? {
                    id: selectedSong.id,
                    title: selectedSong.title || '',
                    albumArtworkURL: selectedSong.albumArtworkURL || null,
                    artistName: artistProfile?.artistName || '',
                    artistId: artistId,
                    audioUrl: selectedSong.audioUrl || null
                } : null,
                targetAudience,
            };

            // 3. Conditional execution based on Edit vs. Create Mode
            if (isEditMode) {
                // 🔑 EDIT MODE: Call updateCampaign (updates post silently without sending notifications)
                const updateCampaign = httpsCallable(functions, 'updateCampaign');
                await updateCampaign({
                    postId: postToEdit.id,
                    ...payload
                });
                toast.success("Announcement updated successfully!");
            } else {
                // 🔑 CREATE MODE: Call launchCampaign (creates new post and notifies subscribers)
                const launchCampaign = httpsCallable(functions, 'launchCampaign');
                await launchCampaign(payload);
                toast.success("Your announcement has been published!");
            }

            onClose();

        } catch (error) {
            console.error(isEditMode ? "Error updating post:" : "Error publishing post:", error);
            toast.error(`Failed to ${isEditMode ? 'update' : 'publish'} post: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleMediaFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            toast.error(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
            event.target.value = null;
            return;
        }

        if (!file.type.startsWith("image/")) {
            toast.error("Please select an image file.");
            event.target.value = null;
            return;
        }

        setMediaFile(file);
    };

    if (!isOpen) return null;

    const artistDisplayName = artistProfile?.artistName || "Artist";
    const songNamePlaceholder = selectedSong?.title ? `"${selectedSong.title}"` : "latest release";

    return createPortal(
        <AnimatePresence mode="wait">
            {isOpen && (
                <div className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 ${isDarkMode ? 'dark' : ''}`}>
                    
                    {/* Backdrop */}
                    <motion.div
                        className="fixed inset-0 bg-black/80 transform-gpu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15, ease: "linear" }}
                        onClick={onClose}
                    />

                    {/* Modal Card */}
                    <motion.div 
                        className={`relative z-10 rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden border transform-gpu transition-all duration-300 ${
                            isDarkMode 
                                ? "bg-zinc-900 border-zinc-800 text-white shadow-black/60" 
                                : "bg-white border-slate-100 text-slate-900 shadow-slate-200"
                        }`}
                        initial={{ scale: 0.95, opacity: 0, y: 15 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 15 }}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                    >
                        {/* Scrollable Form Body */}
                        <div className="p-6 flex-grow overflow-y-auto custom-scrollbar space-y-5">
                            
                            <h3 className="text-base font-black uppercase tracking-tight italic">
                                {isEditMode ? 'Edit Announcement Details' : 'Publish An Announcement'}
                            </h3>

                            {/* TITLE & BODY INPUTS */}
                            <div className="space-y-4">
                                <div className="space-y-1">
                                    <label className={`block text-[10px] font-black uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-gray-700'}`}>Title</label>
                                    <input 
                                        type="text"
                                        value={postTitle} 
                                        onChange={(e) => setPostTitle(e.target.value)} 
                                        placeholder="A Catchy Headline for Your Post"
                                        className={`w-full p-3 text-xs font-semibold rounded-xl border transition-all focus:outline-none focus:border-brand-pink/50 ${
                                            isDarkMode 
                                                ? "bg-zinc-950 border-zinc-800 text-white placeholder-zinc-600 shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)]" 
                                                : "bg-white border-slate-200 text-slate-900 placeholder-slate-400"
                                        }`}
                                    />
                                </div>

                                <div className="space-y-1">
                                    <label className={`block text-[10px] font-black uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-gray-700'}`}>Body</label>
                                    <textarea 
                                        value={postBody} 
                                        onChange={(e) => setPostBody(e.target.value)} 
                                        placeholder={`e.g., ${artistDisplayName} announces new tour dates...`} 
                                        className={`w-full p-3 text-xs font-semibold rounded-xl border transition-all focus:outline-none focus:border-brand-pink/50 resize-none ${
                                            isDarkMode 
                                                ? "bg-zinc-950 border-zinc-800 text-white placeholder-zinc-600 shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)]" 
                                                : "bg-white border-slate-200 text-slate-900 placeholder-slate-400"
                                        }`}
                                        rows="4"
                                    />
                                </div>
                            </div>

                            {/* PROGRESSIVE AUDIENCE GATING MATRIX */}
                            <div className={`p-3.5 border rounded-xl space-y-2 ${isDarkMode ? 'bg-zinc-950 border-zinc-800' : 'bg-slate-50 border-slate-200'}`}>
                                <p className="text-[9px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                                    Who Can See This Announcement?
                                </p>
                                <div className="flex flex-wrap gap-x-4 gap-y-2 pt-0.5">
                                    {['everyone', 'subscribers', 'fans', 'supafans', 'ultrafans'].map((tierKey) => {
                                        const labelsMap = {
                                            everyone: 'Everyone',
                                            subscribers: 'Subscribers',
                                            fans: 'Fans',
                                            supafans: 'SupaFans',
                                            ultrafans: 'Ultra Fans'
                                        };
                                        
                                        return (
                                            <label 
                                                key={tierKey} 
                                                className="flex items-center text-xs font-bold capitalize select-none h-5 cursor-pointer text-slate-900 dark:text-white"
                                            >
                                                <input 
                                                    type="checkbox" 
                                                    checked={targetAudience[tierKey]}
                                                    onChange={() => handleAudienceToggle(tierKey)}
                                                    className="mr-1.5 h-3.5 w-3.5 rounded border-zinc-300 dark:border-zinc-700 bg-transparent cursor-pointer accent-[#ff00cc] focus:ring-1 focus:ring-[#ff00cc] focus:ring-offset-0"
                                                />
                                                {labelsMap[tierKey]}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                            
                            {/* PRESS RELEASE IMAGE UPLOAD */}
                            <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-950/40 border-zinc-800' : 'bg-slate-50 border-slate-200'}`}>
                                <label className={`block text-[10px] font-black uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-gray-700'}`}>
                                    Upload Press Release Image (Optional)
                                </label>
                                <input 
                                    type="file" 
                                    accept="image/*"
                                    onChange={handleMediaFileChange} 
                                    className={`block w-full text-xs font-semibold cursor-pointer file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-[10px] file:font-black file:uppercase file:tracking-wider ${
                                        isDarkMode 
                                            ? 'text-zinc-500 file:bg-zinc-800 file:text-zinc-300 hover:file:bg-zinc-700' 
                                            : 'text-slate-500 file:bg-slate-200 file:text-slate-700 hover:file:bg-slate-300'
                                    }`} 
                                />
                            </div>

                            {/* EMBED SONG SECTION */}
                            <div className="space-y-2 p-3.5 border rounded-xl border-dashed border-slate-200 dark:border-zinc-800">
                                <label className={`block text-[10px] font-black uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-gray-700'}`}>
                                    Embed Song Track (Optional)
                                </label>

                                <input 
                                    type="text"
                                    value={songBoxTitle}
                                    onChange={(e) => setSongBoxTitle(e.target.value)}
                                    placeholder={`e.g. Stream ${artistDisplayName}'s ${songNamePlaceholder} here!`}
                                    className={`w-full p-2.5 text-xs font-semibold rounded-xl border transition-all focus:outline-none focus:border-brand-pink/50 ${
                                        isDarkMode 
                                            ? "bg-zinc-950 border-zinc-800 text-white placeholder-zinc-600" 
                                            : "bg-white border-slate-200 text-slate-900 placeholder-slate-400"
                                    }`}
                                />
                                
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        disabled={isFetchingSongs}
                                        onChange={handleSearchTermChange}
                                        onBlur={() => setTimeout(() => setIsDropdownVisible(false), 150)}
                                        onFocus={() => setIsDropdownVisible(true)}
                                        placeholder={isFetchingSongs ? "Loading catalog..." : "Search for a song from your discography"}
                                        className={`w-full p-3 text-xs font-semibold rounded-xl border transition-all focus:outline-none focus:border-brand-pink/50 ${
                                            isDarkMode 
                                                ? "bg-zinc-950 border-zinc-800 text-white placeholder-zinc-600 shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)]" 
                                                : "bg-white border-slate-200 text-slate-900 placeholder-slate-400"
                                        } ${isFetchingSongs ? "opacity-60 cursor-wait animate-pulse" : ""}`}
                                    />

                                    <AnimatePresence>
                                        {isDropdownVisible && searchResults.length > 0 && (
                                            <motion.div 
                                                initial={{ opacity: 0, y: 5 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: 5 }}
                                                className={`absolute top-full left-0 right-0 max-h-48 overflow-y-auto border rounded-xl mt-1 shadow-2xl z-20 custom-scrollbar ${
                                                    isDarkMode ? "bg-zinc-950 border-zinc-800 text-white" : "bg-white border-gray-200 text-slate-900"
                                                }`}
                                            >
                                                {searchResults.map(song => (
                                                    <div
                                                        key={song.id}
                                                        onMouseDown={() => handleSelectSong(song)}
                                                        className={`p-3 text-xs font-bold uppercase tracking-wide cursor-pointer transition-colors ${
                                                            isDarkMode ? "hover:bg-zinc-900 text-white" : "hover:bg-gray-50 text-slate-900"
                                                        }`}
                                                    >
                                                        {song.title}
                                                    </div>
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>

                            {/* EXTERNAL URL LINK SECTION */}
                            <div className="space-y-2 p-3.5 border rounded-xl border-dashed border-slate-200 dark:border-zinc-800">
                                <label className={`block text-[10px] font-black uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-gray-700'}`}>
                                    External URL / Web Link (Optional)
                                </label>

                                <input 
                                    type="text"
                                    value={urlBoxTitle}
                                    onChange={(e) => setUrlBoxTitle(e.target.value)}
                                    placeholder={`e.g. Check out ${artistDisplayName}'s link below!`}
                                    className={`w-full p-2.5 text-xs font-semibold rounded-xl border transition-all focus:outline-none focus:border-brand-pink/50 ${
                                        isDarkMode 
                                            ? "bg-zinc-950 border-zinc-800 text-white placeholder-zinc-600" 
                                            : "bg-white border-slate-200 text-slate-900 placeholder-slate-400"
                                    }`}
                                />

                                <input 
                                    type="url"
                                    value={externalUrl} 
                                    onChange={(e) => setExternalUrl(e.target.value)} 
                                    placeholder="https://your-website.com/tickets"
                                    className={`w-full p-3 text-xs font-semibold rounded-xl border transition-all focus:outline-none focus:border-brand-pink/50 ${
                                        isDarkMode 
                                            ? "bg-zinc-950 border-zinc-800 text-white placeholder-zinc-600 shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)]" 
                                            : "bg-white border-slate-200 text-slate-900 placeholder-slate-400"
                                    }`}
                                />
                            </div>

                            {/* POST PREVIEW (BOTTOM) */}
                            <div className="pt-2">
                                <label className={`block text-[10px] font-black uppercase tracking-wider mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-gray-700'}`}>
                                    Post Preview
                                </label>
                                <PressPostPreview 
                                    artistName={artistProfile?.artistName}
                                    artistPfp={artistProfile?.photoURL}
                                    title={postTitle}
                                    text={postBody}
                                    mediaFile={mediaFile}
                                    selectedSong={selectedSong}
                                    songBoxTitle={songBoxTitle}
                                    externalUrl={externalUrl}
                                    urlBoxTitle={urlBoxTitle}
                                />
                            </div>
                        </div>
                        
                        {/* ACTION BUTTONS FOOTER */}
                        <div className={`p-4 flex justify-end items-center border-t flex-shrink-0 rounded-b-2xl ${
                            isDarkMode ? 'bg-zinc-950/40 border-zinc-800' : 'bg-slate-50 border-slate-100'
                        }`}>
                            <button 
                                onClick={onClose} 
                                className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-colors focus:outline-none ${
                                    isDarkMode ? "text-zinc-400 hover:text-white hover:bg-zinc-800" : "text-gray-600 hover:bg-gray-100"
                                }`}
                            >
                                Cancel
                            </button>
                            
                            <button 
                                onClick={handlePublishPost} 
                                disabled={isSubmitting} 
                                className={`px-5 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all shadow-md min-w-[84px] flex items-center justify-center active:scale-95 disabled:pointer-events-none ${
                                    isSubmitting
                                        ? "bg-zinc-700 text-zinc-500"
                                        : isDarkMode
                                            ? "bg-brand-pink text-white hover:brightness-110"
                                            : "bg-pink-600 text-white hover:bg-pink-700"
                                }`}
                            >
                                {isSubmitting ? <Spinner /> : (isEditMode ? 'Update' : 'Publish')}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default CampaignBuilderModal;